import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

/**
 * Slice de Observability (panel Global Admin de soporte).
 *
 * Mantiene 4 datasets independientes — workers, impresoras, printJobs y
 * outbox — más un resumen agregado. Cada uno tiene su propio loading/error
 * para que cargar uno no bloquee la UI de los demás. El polling lo hace
 * la vista (no hay timers aquí dentro).
 */
const initialState = {
  summary: { data: null, isLoading: false, error: null },
  workers: { items: [], isLoading: false, error: null },
  printers: { items: [], isLoading: false, error: null },
  printJobs: { items: [], isLoading: false, error: null },
  outbox: { items: [], isLoading: false, error: null },
  communications: { items: [], isLoading: false, error: null },
  costSummary: { data: null, isLoading: false, error: null },
};

const slice = createSlice({
  name: "observability",
  initialState,
  reducers: {
    startLoading(state, action) {
      const key = action.payload;
      if (state[key]) {
        state[key].isLoading = true;
        state[key].error = null;
      }
    },
    hasError(state, action) {
      const { key, error } = action.payload;
      if (state[key]) {
        state[key].isLoading = false;
        state[key].error = error;
      }
    },
    setSummary(state, action) {
      state.summary.isLoading = false;
      state.summary.data = action.payload;
    },
    setWorkers(state, action) {
      state.workers.isLoading = false;
      state.workers.items = action.payload || [];
    },
    setPrinters(state, action) {
      state.printers.isLoading = false;
      state.printers.items = action.payload || [];
    },
    setPrintJobs(state, action) {
      state.printJobs.isLoading = false;
      state.printJobs.items = action.payload || [];
    },
    setOutbox(state, action) {
      state.outbox.isLoading = false;
      state.outbox.items = action.payload || [];
    },
    setCommunications(state, action) {
      state.communications.isLoading = false;
      state.communications.items = action.payload || [];
    },
    setCostSummary(state, action) {
      state.costSummary.isLoading = false;
      state.costSummary.data = action.payload;
    },
  },
});

export default slice.reducer;

export function fetchObservabilitySummary() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("summary"));
    try {
      const res = await axios.get("/observability/summary");
      dispatch(slice.actions.setSummary(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "summary", error: e?.message || "Error" }));
    }
  };
}

export function fetchObservabilityWorkers(limit = 200) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("workers"));
    try {
      const res = await axios.get(`/observability/workers?limit=${limit}`);
      dispatch(slice.actions.setWorkers(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "workers", error: e?.message || "Error" }));
    }
  };
}

export function fetchObservabilityPrinters(limit = 500) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("printers"));
    try {
      const res = await axios.get(`/observability/printers?limit=${limit}`);
      dispatch(slice.actions.setPrinters(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "printers", error: e?.message || "Error" }));
    }
  };
}

// `state`: opcional, 0..4 (Pending/InProgress/Done/Failed/Dead).
// `hours`: ventana temporal, default 24h.
export function fetchObservabilityPrintJobs({ limit = 200, state = null, hours = 24 } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("printJobs"));
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("hours", String(hours));
      if (state != null) params.set("state", String(state));
      const res = await axios.get(`/observability/print-jobs?${params.toString()}`);
      dispatch(slice.actions.setPrintJobs(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "printJobs", error: e?.message || "Error" }));
    }
  };
}

// OutboxController ya existe — endpoint /api/outbox/recent. Lo consumimos
// directamente desde aquí para no duplicar slices. El payload es similar.
export function fetchObservabilityOutbox(limit = 200) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("outbox"));
    try {
      const res = await axios.get(`/outbox/recent?limit=${limit}`);
      dispatch(slice.actions.setOutbox(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "outbox", error: e?.message || "Error" }));
    }
  };
}

// Coste estimado de comunicaciones por restaurante para un mes (YYYY-MM).
// Si no se pasa `month`, el backend usa el mes actual.
export function fetchObservabilityCostSummary({ month = null } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("costSummary"));
    try {
      const params = month ? `?month=${month}` : "";
      const res = await axios.get(`/observability/communications/cost-summary${params}`);
      dispatch(slice.actions.setCostSummary(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "costSummary", error: e?.message || "Error" }));
    }
  };
}

// Comunicaciones (BillingUsage) — emails + whatsapps cross-system.
// `type` opcional: "EMAIL" | "WHATSAPP".
export function fetchObservabilityCommunications({ limit = 200, type = null, hours = 24 } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading("communications"));
    try {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      params.set("hours", String(hours));
      if (type) params.set("type", type);
      const res = await axios.get(`/observability/communications?${params.toString()}`);
      dispatch(slice.actions.setCommunications(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError({ key: "communications", error: e?.message || "Error" }));
    }
  };
}
